// Componente de skeleton para la vista de órdenes - Skeleton component for order view
// Muestra placeholders que respetan la estructura de los bloques de la página
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Skeleton } from '@shared/components/ui/shadcn/skeleton';

interface ViewOrdersSkeletonProps {
  showPreparation?: boolean;
}

export const ViewOrdersSkeleton = ({ showPreparation = false }: ViewOrdersSkeletonProps) => {
  const milestoneCount = showPreparation ? 4 : 3;
  return (
    <>
      {/* Header Buttons Skeleton - Skeleton de los botones del header */}
      <div className="flex justify-end gap-2 absolute top-2 right-6">
        <Skeleton className="h-9 w-9 rounded-md bg-hover-press" />
        <Skeleton className="h-9 w-9 rounded-md bg-hover-press" />
      </div>

      <div className="py-4 px-6">
        <div className="flex flex-col md:flex-row justify-between items-start gap-6">
          {/* Columna Izquierda - Left Column */}
          <div className="w-full md:w-[70%] flex flex-col gap-5">
            {/* OrderProgress Skeleton - Skeleton del avance del pedido */}
            <SectionBox className="bg-card-bg">
              <div className={`grid gap-2 ${milestoneCount === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                {Array.from({ length: milestoneCount }, (_, index) => index).map((index) => (
                  <div key={index} className="flex flex-col items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-20 rounded-full bg-hover-press" />
                    <Skeleton className="h-3.5 w-24 rounded-full bg-hover-press" />
                  </div>
                ))}
              </div>
            </SectionBox>

            {/* OrderInfo Skeleton - Skeleton de información del pedido */}
            <SectionBox className="bg-card-bg">
              <Skeleton className="mb-4 h-6 w-48 rounded-full bg-hover-press" />
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full bg-hover-press" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-24 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-36 rounded-full bg-hover-press" />
                  </div>
                </div>
              </div>
            </SectionBox>

            {/* SelectedItems Skeleton - Skeleton de items seleccionados */}
            <SectionBox className="bg-card-bg">
              <div className="flex justify-between items-center mb-4">
                <Skeleton className="h-6 w-40 rounded-full bg-hover-press" />
                <Skeleton className="h-8 w-8 rounded-md bg-hover-press" />
              </div>
              <div className="space-y-4 pt-4">
                {/* Encabezados de tabla - Table headers */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 pb-2 border-b border-card-border">
                  <Skeleton className="h-4 w-32 rounded-full col-span-6 bg-hover-press" />
                  <Skeleton className="h-4 w-24 rounded-full col-span-3 bg-hover-press" />
                  <Skeleton className="h-4 w-20 rounded-full col-span-2 bg-hover-press" />
                  <Skeleton className="h-4 w-4 rounded-full col-span-1 bg-hover-press" />
                </div>

                {/* Items de productos - Product items */}
                <div className="space-y-4">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="border-b border-card-border py-2">
                      {/* Desktop Layout - Diseño de escritorio */}
                      <div className="hidden md:grid md:grid-cols-12 gap-4 items-center py-2">
                        <div className="col-span-6">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-12 w-12 rounded-md bg-hover-press" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-32 rounded-full bg-hover-press" />
                              <Skeleton className="h-3 w-24 rounded-full bg-hover-press" />
                            </div>
                          </div>
                        </div>
                        <div className="col-span-3">
                          <Skeleton className="h-4 w-20 rounded-full bg-hover-press" />
                        </div>
                        <div className="col-span-2">
                          <Skeleton className="h-4 w-12 rounded-full bg-hover-press" />
                        </div>
                        <div className="col-span-1" />
                      </div>

                      {/* Mobile Layout - Diseño móvil */}
                      <div className="md:hidden space-y-3">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-12 w-12 rounded-md bg-hover-press" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-32 rounded-full bg-hover-press" />
                            <Skeleton className="h-3 w-24 rounded-full bg-hover-press" />
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-4 w-20 rounded-full flex-1 bg-hover-press" />
                          <Skeleton className="h-4 w-12 rounded-full flex-1 bg-hover-press" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Resumen financiero - Financial summary */}
                <div className="space-y-3 pt-2 border-t border-card-border">
                  <div className="flex justify-between items-center pt-3">
                    <Skeleton className="h-4 w-20 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-24 rounded-full bg-hover-press" />
                  </div>
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-4 w-28 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-20 rounded-full bg-hover-press" />
                  </div>
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-4 w-20 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-20 rounded-full bg-hover-press" />
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-card-border">
                    <Skeleton className="h-5 w-16 rounded-full bg-hover-press" />
                    <Skeleton className="h-5 w-28 rounded-full bg-hover-press" />
                  </div>
                </div>
              </div>
            </SectionBox>
          </div>

          {/* Columna Derecha - Right Column */}
          <div className="w-full md:w-[30%] flex flex-col gap-5">
            {/* ShippingInfo Skeleton - Skeleton de información de envío */}
            <SectionBox className="bg-card-bg">
              <div className="flex justify-between items-center mb-4">
                <Skeleton className="h-6 w-36 rounded-full bg-hover-press" />
                <Skeleton className="h-8 w-8 rounded-md bg-hover-press" />
              </div>
              <div className="pt-6 flex flex-col gap-6">
                <div>
                  <Skeleton className="h-5 w-24 rounded-full mb-3 bg-hover-press" />
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-4 w-full rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-3/4 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-2/3 rounded-full bg-hover-press" />
                  </div>
                </div>
                <div className="border-t border-card-border" />
                <div>
                  <Skeleton className="h-5 w-28 rounded-full mb-3 bg-hover-press" />
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-4 w-full rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-5/6 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-4/5 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-3/4 rounded-full bg-hover-press" />
                  </div>
                </div>
              </div>
            </SectionBox>

            {/* AdditionalInfo Skeleton - Skeleton de información adicional */}
            <SectionBox className="bg-card-bg">
              <div className="flex justify-between items-center mb-4">
                <Skeleton className="h-6 w-40 rounded-full bg-hover-press" />
                <Skeleton className="h-8 w-8 rounded-md bg-hover-press" />
              </div>
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full bg-hover-press" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32 rounded-full bg-hover-press" />
                    <Skeleton className="h-4 w-28 rounded-full bg-hover-press" />
                  </div>
                </div>
              </div>
            </SectionBox>
          </div>
        </div>
      </div>
    </>
  );
};
